import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryResultsStore, useQueryPanelStore } from '@/stores';
import { useQueryFiltersStore } from '@/stores/query-filters.store';
import {
    buildUrlStateFromStores,
    serializeToUrlParams,
} from '@/utils/query-url-params';
import { DateTimeMode, SortOption } from '@/utils/query-filters';
import { AppRoutes } from '@/constants/routes';
import { useRecentQueries } from './useRecentQueries';
import type { QueryPanelDraft } from '@/stores/query-panel.store';

interface UseQuerySearchOptions {
    compact?: boolean;
    uploadAttachment?: () => Promise<
        { url: string; filename: string } | undefined
    >;
    hasAttachment?: boolean;
    isUploading?: boolean;
    draft: QueryPanelDraft;
    isVideoSourceSelected?: boolean;
    onMissingSources?: () => void;
}

export function useQuerySearch(options: UseQuerySearchOptions) {
    const {
        compact = false,
        uploadAttachment,
        hasAttachment = false,
        isUploading = false,
        draft,
        isVideoSourceSelected = false,
        onMissingSources,
    } = options;
    const navigate = useNavigate();
    const { setDemo, clearEvents } = useQueryResultsStore();
    const {
        recentQueries,
        addRecentQuery,
        removeRecentQuery,
        clearRecentQueries,
    } = useRecentQueries();

    const { isMinimized, setIsMinimized, searchQuery, setSearchQuery } =
        useQueryPanelStore();
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const hasMounted = useRef(false);

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        if (compact && !isMinimized) {
            inputRef.current?.focus();
        }
    }, [isMinimized, compact]);

    const hasSourceSelection =
        draft.draftSources.all || draft.draftSources.selected.size > 0;

    const handleSearch = async () => {
        if (isUploading) return;

        if (!hasSourceSelection) {
            onMissingSources?.();
            return;
        }

        const attachment =
            hasAttachment && uploadAttachment
                ? await uploadAttachment()
                : undefined;

        if (!searchQuery.trim() && !attachment) return;

        if (searchQuery.trim()) {
            addRecentQuery(searchQuery);
        }

        const filters = isVideoSourceSelected
            ? { ...draft.draftFilters, dateTime: { mode: DateTimeMode.ALL } }
            : draft.draftFilters;

        const urlState = buildUrlStateFromStores(
            searchQuery,
            {
                all: draft.draftSources.all,
                selected: draft.draftSources.selected,
                selectedFolders: draft.draftSources.selectedFolders,
                excluded: draft.draftSources.excluded,
            },
            filters,
            SortOption.CONFIDENCE,
            attachment,
            Array.from(draft.draftObjectClassIds),
        );

        const params = serializeToUrlParams(urlState, new URLSearchParams());
        params.set('_t', String(Date.now()));
        clearEvents();
        navigate(`${AppRoutes.QUERY_RESULTS}?${params.toString()}`);

        if (compact) {
            setIsMinimized(true);
        }
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            setDemo(true);
            handleSearch();
        }
    };

    const handleClickSearch = () => {
        setDemo(true);
        handleSearch();
    };

    const handleMinimize = () => {
        if (compact) {
            setIsMinimized(true);
        }
    };

    const handleFocus = () => {
        if (compact && isMinimized) {
            setIsMinimized(false);
        }
    };

    const focusAndExpand = useCallback(() => {
        if (compact && isMinimized) {
            setIsMinimized(false);
        }
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    }, [compact, isMinimized, setIsMinimized]);

    return {
        searchQuery,
        setSearchQuery,
        isMinimized,
        inputRef,
        canSearch: !isUploading && (!!searchQuery.trim() || hasAttachment),
        handleSearch: handleClickSearch,
        handleKeyDown,
        handleMinimize,
        handleFocus,
        focusAndExpand,
        recentQueries,
        removeRecentQuery,
        clearRecentQueries,
    };
}
